import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { flowService } from '../services/flowService';
import { useAuth } from './AuthContext';
import { useEnvironment } from './EnvironmentContext';

const FlowContext = createContext();

export const useFlow = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error('useFlow must be used within a FlowProvider');
  }
  return context;
};

export const FlowProvider = ({ children }) => {
  const [flows, setFlows] = useState([]);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedFlows, setHasLoadedFlows] = useState(false);
  const [error, setError] = useState(null);

  // Get auth and environment from other contexts
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();

  // Load flows when environment changes
  useEffect(() => {
    setHasLoadedFlows(false);
    if (selectedEnvironment?.id) {
      loadFlows();
    } else {
      setFlows([]);
    }
  }, [selectedEnvironment?.id]);

  // Load all flows from backend for current environment
  const loadFlows = useCallback(async () => {
    if (!selectedEnvironment?.id) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const flowsData = await flowService.listFlows(selectedEnvironment.id, authenticatedFetch);
      setFlows(flowsData);
      setHasLoadedFlows(true);
    } catch (error) {
      console.error('Error loading flows:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Create a new flow
  const createFlow = useCallback(async (flowData) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const newFlow = await flowService.createFlow(
        selectedEnvironment.id,
        {
          name: flowData.name,
          description: flowData.description || '',
          flowJson: flowData.flowJson,
          flowType: flowData.flowType,
          endpointUri: flowData.endpointUri,
          categories: flowData.categories,
          agentMeta: flowData.agentMeta
        },
        authenticatedFetch
      );

      setFlows(prev => [...prev, newFlow]);
      return newFlow;
    } catch (error) {
      console.error('Error creating flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Update an existing flow
  const updateFlow = useCallback(async (flowId, updates) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const flow = flows.find(f => f.id === flowId);
      if (!flow) {
        throw new Error('Flow not found');
      }

      const updatedFlow = await flowService.updateFlow(
        selectedEnvironment.id,
        flowId,
        {
          name: updates.name || flow.name,
          description: updates.description !== undefined ? updates.description : flow.description,
          flowJson: updates.flowJson || flow.flowJson,
          flowType: updates.flowType !== undefined ? updates.flowType : flow.flowType,
          endpointUri: updates.endpointUri !== undefined ? updates.endpointUri : flow.endpointUri,
          categories: updates.categories !== undefined ? updates.categories : flow.categories,
          agentMeta: updates.agentMeta !== undefined ? updates.agentMeta : flow.agentMeta
        },
        authenticatedFetch
      );

      setFlows(prev => prev.map(f => f.id === flowId ? updatedFlow : f));

      if (currentFlow?.id === flowId) {
        setCurrentFlow(updatedFlow);
      }

      return updatedFlow;
    } catch (error) {
      console.error('Error updating flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, flows, currentFlow, authenticatedFetch]);

  // Delete a flow
  const deleteFlow = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.deleteFlow(selectedEnvironment.id, flowId, authenticatedFetch);

      setFlows(prev => prev.filter(f => f.id !== flowId));

      if (currentFlow?.id === flowId) {
        setCurrentFlow(null);
      }

      return { success: true, removedFromWhatsApp: result?.removedFromWhatsApp ?? false };
    } catch (error) {
      console.error('Error deleting flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, currentFlow, authenticatedFetch]);

  // Get a single flow by ID
  const getFlow = useCallback((flowId) => {
    return flows.find(f => f.id === flowId);
  }, [flows]);

  // Load a flow for editing (sets as current flow)
  const loadFlowForEditing = useCallback((flowId) => {
    const flow = flows.find(f => f.id === flowId);
    if (flow) {
      setCurrentFlow(flow);
      return flow;
    }
    return null;
  }, [flows]);

  // Load a flow by ID from the backend (for URL-based navigation)
  const loadFlowById = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      return null;
    }

    // First check if we already have it in local state
    const existingFlow = flows.find(f => f.id === flowId);
    if (existingFlow) {
      setCurrentFlow(existingFlow);
      return existingFlow;
    }

    // Otherwise fetch from backend
    try {
      setIsLoading(true);
      setError(null);
      const flow = await flowService.getFlow(selectedEnvironment.id, flowId, authenticatedFetch);
      setCurrentFlow(flow);
      // Add to flows array if not present
      setFlows(prev => {
        if (!prev.find(f => f.id === flow.id)) {
          return [...prev, flow];
        }
        return prev;
      });
      return flow;
    } catch (error) {
      console.error('Error loading flow by ID:', error);
      setError(error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, flows, authenticatedFetch]);

  // Re-fetch a single flow's latest state (e.g. background sync/validation status)
  // without toggling global loading. Merges the result into local state and returns it.
  const refreshFlow = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      return null;
    }
    try {
      const flow = await flowService.getFlow(selectedEnvironment.id, flowId, authenticatedFetch);
      // Merge only sync/validation status, NOT flowJson/name/description/updatedAt, so an
      // in-flight poll never overwrites edits the user made after the save that started it.
      const statusFields = {
        syncStatus: flow.syncStatus,
        validationStatus: flow.validationStatus,
        validationErrors: flow.validationErrors,
        syncError: flow.syncError,
        status: flow.status,
        whatsappFlowId: flow.whatsappFlowId,
        whatsappStatus: flow.whatsappStatus,
      };
      setFlows(prev => prev.map(f => (f.id === flow.id ? { ...f, ...statusFields } : f)));
      setCurrentFlow(prev => (prev?.id === flow.id ? { ...prev, ...statusFields } : prev));
      return flow;
    } catch (error) {
      console.error('Error refreshing flow:', error);
      return null;
    }
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Duplicate a flow within the current environment; prepends the copy to the list.
  const duplicateFlow = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }
    const copy = await flowService.duplicateFlow(selectedEnvironment.id, flowId, authenticatedFetch);
    setFlows(prev => [copy, ...prev]);
    return copy;
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Move a flow to a different environment; it leaves the current list.
  const moveFlow = useCallback(async (flowId, targetEnvironmentId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }
    const moved = await flowService.moveFlow(selectedEnvironment.id, flowId, targetEnvironmentId, authenticatedFetch);
    setFlows(prev => prev.filter(f => f.id !== flowId));
    return moved;
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Load a flow's saved version history (newest first).
  const loadVersions = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      return [];
    }
    try {
      return await flowService.getVersions(selectedEnvironment.id, flowId, authenticatedFetch);
    } catch (error) {
      console.error('Error loading version history:', error);
      throw error;
    }
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Restore a flow to a previous version; returns the restored flow (with the past JSON).
  const restoreVersion = useCallback(async (flowId, versionNumber) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }
    const restored = await flowService.restoreVersion(selectedEnvironment.id, flowId, versionNumber, authenticatedFetch);
    setFlows(prev => prev.map(f => (f.id === restored.id ? restored : f)));
    setCurrentFlow(prev => (prev?.id === restored.id ? restored : prev));
    return restored;
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Report our builder's validation verdict for a flow (best-effort telemetry; ADR-0006).
  const reportValidationFeedback = useCallback((flowId, builderErrorCount) => {
    if (!selectedEnvironment?.id) {
      return;
    }
    flowService.reportValidationFeedback(selectedEnvironment.id, flowId, builderErrorCount, authenticatedFetch);
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Deploy a flow to WhatsApp
  const deployToWhatsApp = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.deployFlow(selectedEnvironment.id, flowId, authenticatedFetch);

      // Refresh flows to get updated WhatsApp data
      await loadFlows();

      return result;
    } catch (error) {
      console.error('Error deploying flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch, loadFlows]);

  // Publish a flow on WhatsApp (make it live)
  const publishFlow = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.publishFlow(selectedEnvironment.id, flowId, authenticatedFetch);

      // Refresh flows to get updated status
      await loadFlows();

      return result;
    } catch (error) {
      console.error('Error publishing flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch, loadFlows]);

  // Sync flows from WhatsApp
  const syncFlows = useCallback(async () => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.syncFlows(selectedEnvironment.id, authenticatedFetch);

      // Refresh flows after sync
      await loadFlows();

      return result;
    } catch (error) {
      console.error('Error syncing flows:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch, loadFlows]);

  // Deprecate a published flow on WhatsApp
  const deprecateFlow = useCallback(async (flowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.deprecateFlow(selectedEnvironment.id, flowId, authenticatedFetch);

      // Refresh flows to get updated status
      await loadFlows();

      return result;
    } catch (error) {
      console.error('Error deprecating flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch, loadFlows]);

  // Test a data-exchange endpoint (reachability + optional encrypted handshake)
  const testEndpoint = useCallback(async ({ endpointUri, publicKeyPem }) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    return await flowService.testEndpoint(
      selectedEnvironment.id,
      { endpointUri, publicKeyPem },
      authenticatedFetch
    );
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // List flows from WABA that can be imported
  const listImportableFlows = useCallback(async () => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setError(null);
      return await flowService.listImportableFlows(selectedEnvironment.id, authenticatedFetch);
    } catch (error) {
      console.error('Error listing importable flows:', error);
      setError(error.message);
      throw error;
    }
  }, [selectedEnvironment?.id, authenticatedFetch]);

  // Import a flow from WABA
  const importFlow = useCallback(async (whatsappFlowId) => {
    if (!selectedEnvironment?.id) {
      throw new Error('No environment selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await flowService.importFlow(selectedEnvironment.id, whatsappFlowId, authenticatedFetch);

      // Refresh flows after import
      await loadFlows();

      return result;
    } catch (error) {
      console.error('Error importing flow:', error);
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, authenticatedFetch, loadFlows]);

  const value = {
    // State
    flows,
    currentFlow,
    isLoading,
    hasLoadedFlows,
    error,

    // Flow management
    loadFlows,
    createFlow,
    updateFlow,
    deleteFlow,
    getFlow,
    setCurrentFlow,
    loadFlowForEditing,
    loadFlowById,
    refreshFlow,
    reportValidationFeedback,
    loadVersions,
    restoreVersion,
    duplicateFlow,
    moveFlow,

    // WhatsApp operations
    deployToWhatsApp,
    publishFlow,
    deprecateFlow,
    syncFlows,
    listImportableFlows,
    importFlow,
    testEndpoint,

    // Helper
    setIsLoading,
    setError,
  };

  return (
    <FlowContext.Provider value={value}>
      {children}
    </FlowContext.Provider>
  );
};

export default FlowContext;
